import { defineAsyncComponent } from '../../lib/utils/vue3compat/vue.js?vue3';
import { GlLink, GlButton, GlTooltipDirective } from '../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { MountingPortal } from '../../lib/utils/vue3compat/portal_vue_vue3.js?vue3';
import { __ } from '~/locale';
import deleteWorkItemMutation from '~/work_items/graphql/delete_work_item.mutation.graphql';
import { TYPE_ISSUE } from '~/issues/constants';
import { DETAIL_VIEW_QUERY_PARAM_NAME, DETAIL_VIEW_DESIGN_VERSION_PARAM_NAME, WORK_ITEM_TYPE_ROUTE_WORK_ITEM } from '~/work_items/constants';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import { visitUrl, setUrlParams, updateHistory, removeParams, relativePathToAbsolute, getBaseURL } from '~/lib/utils/url_utility';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import DynamicPanel from '../../vue_shared/components/dynamic_panel.vue?vue3';
import { glListenersMixin } from '~/lib/utils/vue3compat/gl_listeners_mixin';
import { makeDetailPanelItemFullPath, makeDetailPanelUrlParam, canRouterNav, getRequestedPanel } from '../utils.js?vue3';
import WorkItemMetadataProvider from './work_item_metadata_provider.vue?vue3';
export default {
    name: 'WorkItemDetailPanel',
    directives: {
        GlTooltip: GlTooltipDirective
    },
    components: {
        DynamicPanel,
        GlLink,
        GlButton,
        MountingPortal,
        WorkItemDetail: defineAsyncComponent(()=>import('../../../../../ee/app/assets/javascripts/work_items/components/work_item_detail.vue?vue3')),
        WorkItemMetadataProvider
    },
    mixins: [
        glListenersMixin
    ],
    inject: {
        preventRouterNav: {
            default: false
        },
        isGroup: {},
        fullPath: {}
    },
    provide () {
        return {
            viewContext: this.viewContext
        };
    },
    inheritAttrs: false,
    props: {
        open: {
            type: Boolean,
            required: true
        },
        activeItem: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        issuableType: {
            type: String,
            required: false,
            default: TYPE_ISSUE
        },
        isBoard: {
            type: Boolean,
            required: false,
            default: false
        },
        viewContext: {
            type: String,
            required: true
        }
    },
    emits: [
        'work-item-deleted',
        'close',
        'work-item-updated',
        'work-item-type-changed'
    ],
    data () {
        return {
            copyTooltipText: this.$options.i18n.copyTooltipText,
            isWaitingForMutation: false
        };
    },
    computed: {
        activeItemFullPath () {
            return makeDetailPanelItemFullPath(this.activeItem, this.fullPath, this.issuableType);
        },
        headerReference () {
            const path = this.activeItemFullPath.substring(this.activeItemFullPath.lastIndexOf('/') + 1);
            return `${path}#${this.activeItem?.iid}`;
        },
        itemWebUrl () {
            // eslint-disable-next-line local-rules/no-web-url
            return this.activeItem?.webPath || this.activeItem?.webUrl;
        },
        itemAbsoluteUrl () {
            return relativePathToAbsolute(this.itemWebUrl, getBaseURL());
        }
    },
    watch: {
        activeItem: {
            deep: true,
            immediate: true,
            handler (newValue, oldValue) {
                if (newValue?.iid) {
                    this.setDetailPanelParams();
                    // focus on header link when detail-panel is updated
                    this.$nextTick(()=>{
                        if (!oldValue || oldValue?.iid !== newValue?.iid) {
                            this.focusOnHeaderLink();
                        }
                    });
                }
            }
        },
        open: {
            immediate: true,
            handler (newValue) {
                if (newValue) {
                    // focus on header link when detail-panel is updated
                    this.$nextTick(()=>{
                        this.focusOnHeaderLink();
                    });
                }
            }
        }
    },
    mounted () {
        document.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('popstate', this.handlePopState);
    },
    beforeDestroy () {
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('popstate', this.handlePopState);
    },
    methods: {
        async deleteWorkItem ({ workItemId }) {
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: deleteWorkItemMutation,
                    variables: {
                        input: {
                            id: workItemId
                        }
                    }
                });
                if (data.workItemDelete.errors?.length) {
                    throw new Error(data.workItemDelete.errors[0]);
                }
                this.$emit('work-item-deleted', {
                    id: workItemId
                });
            } catch (error) {
                Sentry.captureException(error);
            }
        },
        redirectToWorkItem (e) {
            const workItem = this.activeItem;
            if (e.metaKey || e.ctrlKey) {
                return;
            }
            e.preventDefault();
            const shouldRouterNav = !this.preventRouterNav && !this.isBoard && this.$router && canRouterNav({
                fullPath: this.fullPath,
                webUrl: workItem.webUrl,
                isGroup: this.isGroup,
                issueAsWorkItem: !this.isGroup
            });
            if (shouldRouterNav) {
                this.$router.push({
                    name: 'workItem',
                    params: {
                        iid: workItem.iid,
                        type: WORK_ITEM_TYPE_ROUTE_WORK_ITEM
                    }
                });
            } else {
                visitUrl(this.itemWebUrl);
            }
        },
        handleCopyToClipboard () {
            this.copyTooltipText = this.$options.i18n.copiedTooltipText;
            setTimeout(()=>{
                this.copyTooltipText = this.$options.i18n.copyTooltipText;
            }, 2000);
        },
        handlePopState () {
            if (this.open && !getRequestedPanel()) {
                this.handleClose(true);
            }
        },
        setDetailPanelParams () {
            const params = makeDetailPanelUrlParam(this.activeItem, this.fullPath, this.issuableType);
            if (getRequestedPanel() === params) return;
            updateHistory({
                // we're using `show` to match the modal view parameter
                url: setUrlParams({
                    [DETAIL_VIEW_QUERY_PARAM_NAME]: params
                })
            });
        },
        handleClose (isClickedOutside, bypassPendingRequests = false) {
            const { queryManager } = this.$apollo.provider.clients.defaultClient;
            // We only need this check when the user is on a board and the mutation is pending.
            this.isWaitingForMutation = this.isBoard && window.pendingApolloRequests - queryManager.inFlightLinkObservables.size > 0;
            /* Do not close when a modal is open, or when the user is focused in an editor/input.
       */ if (this.isWaitingForMutation && !bypassPendingRequests || document.body.classList.contains('modal-open') || document.body.classList.contains('image-lightbox-open') || document.activeElement?.closest('.js-editor') != null || document.activeElement.classList.contains('gl-form-input')) {
                return;
            }
            if (getRequestedPanel()) {
                updateHistory({
                    url: removeParams([
                        DETAIL_VIEW_QUERY_PARAM_NAME,
                        DETAIL_VIEW_DESIGN_VERSION_PARAM_NAME
                    ])
                });
            }
            if (!isClickedOutside) {
                document.getElementById(`listItem-${this.activeItemFullPath}/${getIdFromGraphQLId(this.activeItem?.id)}`)?.focus();
            }
            this.$emit('close');
        },
        focusOnHeaderLink () {
            this.$refs?.workItemUrl?.$el?.focus();
        },
        handleWorkItemUpdated (e) {
            this.$emit('work-item-updated', e);
            // Force to close the detail-panel after 100ms even if requests are still pending
            // to not let UI hanging.
            if (this.isWaitingForMutation) {
                setTimeout(()=>{
                    this.handleClose(false, true);
                }, 100);
            }
        },
        handleKeydown ({ key, target }) {
            // A modal can be torn down before this listener runs, which defeats the
            // `modal-open` check in handleClose, so check where the key came from instead.
            if (key === 'Escape' && this.open && !target.closest?.('.modal')) {
                this.handleClose();
            }
        }
    },
    i18n: {
        copyTooltipText: __('Copy item URL'),
        copiedTooltipText: __('Copied')
    }
};
